import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * EmailCredentials Component
 * Main container component for the email credentials workflow
 * Uses a stacked layout where each step remains visible as the process progresses
 */
import { useEffect } from "react";
import { useEmailCredentials } from "../hooks/useEmailCredentials";
import { FileUpload } from "./FileUpload";
import { WorkflowHeader } from "./WorkflowHeader";
import { ValidationStep } from "./ValidationStep";
import { ProcessingStep } from "./ProcessingStep";
import { CompletionStatus } from "./CompletionStatus";
import "../styles/EmailCredentials.css";
export const EmailCredentials = ({ onComplete }) => {
    const credentials = useEmailCredentials();
    // Determine which steps have been reached/completed
    const stepOrder = ["upload", "processing", "complete"];
    const currentStepIndex = stepOrder.indexOf(credentials.currentStep);
    const hasStartedProcessing = currentStepIndex > 0;
    const hasPassedProcessing = currentStepIndex >= 2;
    // Helper functions for row status classes
    const getValidationRowClass = () => {
        if (hasStartedProcessing)
            return "complete";
        return "";
    };
    const getProcessingRowClass = () => {
        if (credentials.processingError)
            return "error";
        if (credentials.currentStep === "processing" && credentials.isProcessing)
            return "in-progress";
        if (hasPassedProcessing)
            return "complete";
        if (hasStartedProcessing)
            return "pending";
        return "";
    };
    // Auto-start processing when we move to processing step
    useEffect(() => {
        if (credentials.currentStep === "processing" && !credentials.isProcessing && credentials.records.length > 0) {
            credentials.startProcessing();
        }
    }, [credentials.currentStep, credentials.isProcessing, credentials.records.length]);
    // Call onComplete callback when workflow completes
    useEffect(() => {
        if (credentials.currentStep === "complete" && onComplete) {
            onComplete();
        }
    }, [credentials.currentStep, onComplete]);
    // Show upload screen if we haven't started processing yet
    if (!hasStartedProcessing && credentials.currentStep !== "error") {
        return (_jsx("div", { className: "email-credentials", children: _jsxs("div", { className: "email-credentials__content", children: [_jsx(WorkflowHeader, {}), _jsx(FileUpload, { onFileSelect: credentials.parseFile, isLoading: credentials.isParsing, error: credentials.parseError })] }) }));
    }
    // Stacked workflow view - show all steps that have been reached
    return (_jsx("div", { className: "email-credentials", children: _jsxs("div", { className: "email-credentials__workflow-stack", children: [_jsx(WorkflowHeader, {}), _jsx(ValidationStep, { recordsCount: credentials.records.length, rowClassName: getValidationRowClass() }), hasStartedProcessing && (_jsx(ProcessingStep, { isProcessing: credentials.isProcessing, currentIndex: credentials.currentProcessingIndex, totalRecords: credentials.totalRecords, processingError: credentials.processingError, hasCompleted: hasPassedProcessing, rowClassName: getProcessingRowClass() })), credentials.currentStep === "complete" && credentials.results && (_jsx(CompletionStatus, { results: credentials.results, onReset: credentials.reset })), credentials.currentStep === "error" && (_jsxs("div", { className: "email-credentials__error-state", children: [_jsx("h3", { children: "Processing Error" }), _jsx("p", { children: credentials.processingError || "An unexpected error occurred" }), _jsx("button", { onClick: credentials.reset, children: "Start Over" })] }))] }) }));
};
export default EmailCredentials;
